import { ENDPOINTS, getFullUrl } from "./config";
import { AppointmentData, UpdateAppointmentData, AppointmentCompletionRequestDTO, AppointmentListingParams, UpdatePaymentMethodPayload } from "./types";

export class AppointmentService {
  static getToken(): string | null {
    return localStorage.getItem("token");
  }
  static getRole(): string {
    return localStorage.getItem("auth_user") || "{}";
  }

  static async createAppointment(appointmentData: AppointmentData) {
    try {
      const response = await fetch(getFullUrl(ENDPOINTS.APPOINTMENT.CREATE), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken() || ""}`,
        },
        body: JSON.stringify(appointmentData), // Convert object to JSON string
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Login failed",
      };
    }
  }

  static async rescheduleAppointment(appointmentData: any) {
    try {
      const response = await fetch(
        getFullUrl(ENDPOINTS.APPOINTMENT.RESCHEDULE),
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json", // Ensure the server knows it's JSON
            Authorization: `Bearer ${this.getToken()}`,
          },
          body: JSON.stringify(appointmentData), // Convert object to JSON string
        },
      );

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Login failed",
      };
    }
  }
  static async findAvailableDoctorslots(date: string, status: string) {
    try {
      const url = `${getFullUrl(
        ENDPOINTS.APPOINTMENT.FINDDOCTORSLOT,
      )}?date=${date}&status=${status}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Delete failed",
      };
    }
  }

  static async getAppointmentListing(
    date: string,
    status?: string,
    id?: string,
  ) {
    try {
      const user: string = this.getRole();
      let baseCase = "";

      if (JSON.parse(user).role === "medicalcamp") {
        baseCase = `campId=${id}`;
      }

      if (JSON.parse(user).role === "clinic") {
        baseCase = `clinicId=${id}`;
      }

      const url = `${getFullUrl(
        ENDPOINTS.APPOINTMENT.FINDAPPOINTMENTLISTING,
      )}?date=${date}&status=${status}&${baseCase}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Fetch listing failed",
      };
    }
  }

  static async cancleAppointment(appointmentId: string, clinicId: string, cancellationReason?: string) {
    try {
      const response = await fetch(
        getFullUrl(
          ENDPOINTS.APPOINTMENT.CANCLE.replace(":appointmentId", appointmentId),
        ),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.getToken()}`,
          },
          body: JSON.stringify({
            appointmentId: appointmentId,
            cancelledById: clinicId,
            cancelledByRole: "Clinic",
            cancellationReason: cancellationReason
          })
        },
      );

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Delete failed",
      };
    }
  }

  static async markAppointmentCompleted(body: AppointmentCompletionRequestDTO) {
    try {
      const response = await fetch(
        getFullUrl(
          ENDPOINTS.APPOINTMENT.MARK_COMPLETED.replace(":appointmentId", body.appointmentId),
        ),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.getToken()}`,
          },
          body: JSON.stringify(body),
        },
      );

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Mark completed failed",
      };
    }
  }

  static async markInConsultation(appointmentId: string) {
    try {
      const response = await fetch(
        getFullUrl(
          ENDPOINTS.APPOINTMENT.MARK_IN_CONSULTATION.replace(":appointmentId", appointmentId),
        ),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.getToken()}`,
          },
          body: JSON.stringify({ appointmentId }),
        },
      );

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to mark appointment in-consultation",
      };
    }
  }

  static async updateAppointmentData(
    appointmentId: string,
    data: UpdateAppointmentData,
  ) {
    try {
      const url = `${getFullUrl(
        ENDPOINTS.APPOINTMENT.UPDATEAPPOINTMENTDATA.replace(
          ":appointmentId",
          appointmentId,
        ),
      )}`;

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Error in fetching patient medications",
      };
    }
  }

  static async updateAppointmentStatus(appointmentId: string, status: string) {
    try {
      const response = await fetch(
        getFullUrl(ENDPOINTS.APPOINTMENT.UPDATEAPPOINTMENTSTATUS),
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json", // Ensure the server knows it's JSON
            Authorization: `Bearer ${this.getToken()}`,
          },
          body: JSON.stringify({ appointmentId, status }), // Convert object to JSON string
        },
      );
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Update failed",
      };
    }
  }

  static async getVidoSDKTOKEN(channelName: string, uid: string) {
    try {
      const url = `${getFullUrl(
        ENDPOINTS.APPOINTMENT.GETVIDEOTOKEN,
      )}?uid=${uid}&channelName=${channelName}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Connection failed",
      };
    }
  }

  static async getPerDayAppointmentCount(id: string) {
    try {
      const response = await fetch(
        getFullUrl(
          ENDPOINTS.APPOINTMENT.GETAPPOINTMENTCOUNTEPERDAY.replace(":id", id),
        ),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        },
      );

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Delete failed",
      };
    }
  }

  static async getAppointmentOverview({
    status = "",
    clinicId = "",
    medicalCampId = "",
    page = 1,
    pageSize = 10,
  }: {
    status?: string;
    clinicId?: string;
    medicalCampId?: string;
    page: number;
    pageSize: number;
  }) {
    try {
      const user = JSON.parse(this.getRole());

      const baseURL = getFullUrl(ENDPOINTS.APPOINTMENT.GETAPPOINTMENTOVERVIEW);
      const url = new URL(baseURL);
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());

      if (user.role === "medicalcamp") {
        url.searchParams.append("medicalCampId", medicalCampId);
      }

      if (user.role === "clinic") {
        url.searchParams.append("clinicId", clinicId);
      }

      if (status) {
        url.searchParams.append("status", status);
      }

      console.log("📡 API Request URL (Appointments):", url.toString());

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
          "Cache-Control": "no-cache",
        },
      });

      const data = await response.json();
      console.log("data", data);
      return data;
    } catch (error) {
      console.error("❌ Error in getAppointmentOverview:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to load appointments",
      };
    }
  }

  static async getDoctorsWithSettings(
    date: string,
    page: number = 1,
    limit: number = 10,
  ) {
    try {
      const url = new URL(
        getFullUrl(`${ENDPOINTS.APPOINTMENT.AVAILABLE_DOCTORS}`),
      );
      url.searchParams.append("date", date);
      url.searchParams.append("page", page.toString());
      url.searchParams.append("limit", limit.toString());

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
          "Content-Type": "application/json",
        },
      });
      return await response.json();
    } catch (error) {
      console.error("Error fetching doctors:", error);
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to fetch doctors",
      };
    }
  }

  static async getDoctorSlots(
    date: string,
    clinicId: string,
    doctorId: string,
  ) {
    try {
      const url = new URL(getFullUrl(`${ENDPOINTS.APPOINTMENT.DAILY_SLOTS}`));
      url.searchParams.append("date", date);
      url.searchParams.append("clinicId", clinicId);
      url.searchParams.append("doctorId", doctorId);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
          "Content-Type": "application/json",
        },
      });
      return await response.json();
    } catch (error) {
      console.error("Error fetching slots:", error);
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to fetch slots",
      };
    }
  }

  static async getNextAvailableDates(doctorId: string, clinicId: string) {
    try {
      const url = new URL(getFullUrl(`${ENDPOINTS.APPOINTMENT.NEXT_AVAILABLE_DATES}`));
      url.searchParams.append("doctorId", doctorId);
      url.searchParams.append("clinicId", clinicId);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
          "Content-Type": "application/json",
        },
      });
      return await response.json();
    } catch (error) {
      console.error("Error fetching next available dates:", error);
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to fetch next available dates",
      };
    }
  }

  static async getAppointmentListingV2({
    status = "",
    dhrId = "",
    clinicId = "",
    page = 1,
    pageSize = 10,
    limit,
    appointmentType = "",
    searchText = "",
    bookedBy = "",
    startDate = "",
    endDate = "",
  }: AppointmentListingParams = {}) {
    try {
      const user = JSON.parse(this.getRole());
      const baseURL = getFullUrl(ENDPOINTS.APPOINTMENT.GET_APPOINTMENT_LISTING_V2);
      const url = new URL(baseURL);

      const itemsPerPage = limit || pageSize || 10;
      url.searchParams.append("page", (page || 1).toString());
      url.searchParams.append("limit", itemsPerPage.toString());
      url.searchParams.append("pageSize", itemsPerPage.toString());

      // If user is admin, we don't pass clinicId unless explicitly selected
      if (user.role !== "admin" && clinicId) {
        url.searchParams.append("clinicId", clinicId);
      } else if (user.role !== "admin" && !clinicId) {
        // If not admin and clinicId not provided, try to use user's own id if they are a clinic/camp
        if (user.role === "clinic" || user.role === "medicalcamp") {
          url.searchParams.append("clinicId", user.id);
        }
      } else if (user.role === "admin" && clinicId) {
        url.searchParams.append("clinicId", clinicId);
      }

      if (status) {
        url.searchParams.append("status", status);
      }

      if (dhrId) {
        url.searchParams.append("dhrId", dhrId);
      }

      if (appointmentType) {
        url.searchParams.append("appointmentType", appointmentType);
      }

      if (searchText) {
        url.searchParams.append("searchText", searchText);
      }

      if (bookedBy) {
        url.searchParams.append("bookedBy", bookedBy);
      }

      if (startDate) {
        url.searchParams.append("startDate", startDate);
      }

      if (endDate) {
        url.searchParams.append("endDate", endDate);
      }

      console.log("📡 API Request URL (Appointments V2):", url.toString());

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      return await response.json();
    } catch (error) {
      console.error("❌ Error in getAppointmentListingV2:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to load appointments",
      };
    }
  }

  static async getAppointmentSummary(appointmentId: string) {
    try {
      const url = getFullUrl(
        ENDPOINTS.APPOINTMENT.GET_SUMMARY.replace(":appointmentId", appointmentId)
      );

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      return await response.json();
    } catch (error) {
      console.error("❌ Error in getAppointmentSummary:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch appointment summary",
      };
    }
  }

  static async updatePendingPayment(data: any) {
    try {
      const response = await fetch(getFullUrl(ENDPOINTS.APPOINTMENT.UPDATE_PENDING_PAYMENT), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(data),
      });

      return await response.json();
    } catch (error) {
      console.error("❌ Error in updatePendingPayment:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to update pending payment",
      };
    }
  }

  static async updatePaymentMethod(data: UpdatePaymentMethodPayload) {
    try {
      const response = await fetch(getFullUrl(ENDPOINTS.APPOINTMENT.UPDATE_PAYMENT_METHOD), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(data),
      });

      return await response.json();
    } catch (error) {
      console.error("❌ Error in updatePaymentMethod:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to update payment method",
      };
    }
  }

  static async getPrescriptionSkeleton(appointmentId: string) {
    try {
      const url = getFullUrl(
        ENDPOINTS.APPOINTMENT.PRESCRIPTION_SKELETON.replace(":appointmentId", appointmentId)
      );

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      return await response.json();
    } catch (error) {
      console.error("❌ Error in getPrescriptionSkeleton:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to fetch prescription skeleton details",
      };
    }
  }

  static async getDoctorAppointmentSettings(doctorId: string) {
    try {
      const url = getFullUrl(
        `${ENDPOINTS.APPOINTMENT.DOCTOR_APPOINTMENT_SETTINGS}?doctorId=${doctorId}`
      );

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
          "Content-Type": "application/json",
        },
      });

      return await response.json();
    } catch (error) {
      console.error("❌ Error in getDoctorAppointmentSettings:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch doctor appointment settings",
      };
    }
  }

  static async uploadPrescription(appointmentId: string, file: File) {
    try {
      const formData = new FormData();
      formData.append("appointmentId", appointmentId);
      formData.append("prescription", file);

      const url = getFullUrl(
        ENDPOINTS.APPOINTMENT.UPLOAD_PRESCRIPTION.replace(":appointmentId", appointmentId)
      );

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: formData,
      });

      return await response.json();
    } catch (error) {
      console.error("❌ Error in uploadPrescription:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to upload prescription",
      };
    }
  }
}


